import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Download, ArrowLeft, Image as ImageIcon, Layers, ChevronDown, ChevronLeft, ChevronRight, GripVertical, Stamp, Trash2, Upload, Move, Eye, ImagePlus, Sun, SlidersHorizontal } from 'lucide-react';
import { useTranslation } from '@rehub/i18n';
import { WatermarkSettings } from '../types';
import Viewer360 from './Viewer360';
import { applyAdjustmentsToImageUrl } from '../utils/imageAdjustments';
import { AIContentDisclosure, getDownloadLabel, type AIContentClassification } from '@rehub/ai-transparency';
import CurvesEditor from './CurvesEditor';
import { CurvesState, applyCurvesToImageUrlAsBlob } from '../services/curves';
import { supabase } from '../services/supabase';
import {
  BrandProfilePicker,
  MediaBrandManager,
  fetchBrandProfiles,
  createBrandProfile,
  updateBrandProfile,
  deleteBrandProfile,
  uploadBrandProfileLogo,
} from '@rehub/branded-viewer';
import type { MediaBrandProfile } from '@rehub/branded-viewer';

interface ResultViewerProps {
  originalUrl: string;
  processedUrl: string;
  onBack: () => void;
  onDownload: (format: 'png' | 'jpeg', watermark?: WatermarkSettings | null, sourceUrl?: string, aiDisclaimer?: boolean) => void;
  /** Called when user clicks Continue Editing. Pass the current image URL to use (includes any brightness/contrast adjustment). */
  onAccept: (imageUrlToUse?: string) => void;
  /** User ID for brand profiles; when set, brand picker is shown in the watermark panel */
  userId?: string | null;
  onSendToHyperdusk?: () => void;
  onSendToVirtualStaging?: () => void;
  onSendToPhotoEdit?: () => void;
  onSendToPhotoplan?: () => void;
  sendToPhotoplanLabel?: string;
  aiOperation?: string;
  aiClassification?: AIContentClassification;
}

const ResultViewer: React.FC<ResultViewerProps> = ({ 
  originalUrl, 
  processedUrl, 
  onBack, 
  onDownload,
  onAccept,
  userId,
  onSendToHyperdusk,
  onSendToVirtualStaging,
  onSendToPhotoEdit,
  onSendToPhotoplan,
  sendToPhotoplanLabel = 'Send to Photoplan',
  aiOperation = 'photoclear_auto',
  aiClassification = 'AI_MODIFIED',
}) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<'compare' | 'processed' | 'original'>('compare');
  const [sliderPosition, setSliderPosition] = useState(50);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [includeAiDisclaimer, setIncludeAiDisclaimer] = useState(false);
  
  // Watermark State
  const [isWatermarking, setIsWatermarking] = useState(false);
  const [watermark, setWatermark] = useState<WatermarkSettings | null>(null);
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);
  
  // 360 Viewer State
  const [is360Image, setIs360Image] = useState(false);
  const [show360Viewer, setShow360Viewer] = useState(false);
  const [viewer360Mode, setViewer360Mode] = useState<'original' | 'processed'>('processed');

  // Brightness / Contrast (quick adjust) state
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [brightness, setBrightness] = useState(0);
  const [contrast, setContrast] = useState(1);
  const [adjustedUrl, setAdjustedUrl] = useState<string | null>(null);
  const [isApplyingAdjustments, setIsApplyingAdjustments] = useState(false);

  // Curves editor state
  const [isCurvesOpen, setIsCurvesOpen] = useState(false);
  const [isSavingCurves, setIsSavingCurves] = useState(false);

  // Brand Profiles State
  const [brands, setBrands] = useState<MediaBrandProfile[]>([]);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [showBrandManager, setShowBrandManager] = useState(false);
  const brandsFetched = useRef(false);

  const loadBrands = useCallback(async () => {
    if (!userId) return [];
    setBrandsLoading(true);
    try {
      const data = await fetchBrandProfiles(supabase, userId);
      setBrands(data);
      return data;
    } finally {
      setBrandsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId && isWatermarking && !brandsFetched.current) {
      brandsFetched.current = true;
      loadBrands();
    }
  }, [userId, isWatermarking, loadBrands]);

  const handleBrandSelect = useCallback((brand: MediaBrandProfile) => {
    if (brand.logo_url) {
      setWatermark({
        imageUrl: brand.logo_url,
        x: brand.logo_position === 'top-right' ? 0.75 : 0.05,
        y: 0.05,
        scale: 0.2,
        opacity: 0.8,
      });
      setViewMode('processed');
    }
  }, []);

  /** Current image to show as result (adjusted or original processed) */
  const displayUrl = adjustedUrl ?? processedUrl;

  /** Live preview filter when Brighten panel is open (see effect before Apply) — CSS only, not baked */
  const previewFilter =
    isAdjusting && (brightness !== 0 || contrast !== 1)
      ? `brightness(${1 + brightness / 100}) contrast(${contrast})`
      : undefined;

  const containerRef = useRef<HTMLDivElement>(null);
  const downloadMenuRef = useRef<HTMLDivElement>(null);
  const processedImageRef = useRef<HTMLImageElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const handleApplyAdjustments = async () => {
    const source = adjustedUrl ?? processedUrl;
    if (!source) return;
    setIsApplyingAdjustments(true);
    try {
      const result = await applyAdjustmentsToImageUrl(source, { brightness, contrast });
      setAdjustedUrl(result);
      setBrightness(0);
      setContrast(1);
    } catch (e) {
      console.error('Apply adjustments failed', e);
      alert('Failed to apply brightness/contrast. Please try again.');
    } finally {
      setIsApplyingAdjustments(false);
    }
  };

  const handleResetAdjustments = () => {
    setAdjustedUrl(null);
    setBrightness(0);
    setContrast(1);
  };

  // Apply Photoshop-style curves to the current image (bakes into adjustedUrl).
  const handleSaveCurves = async (curves: CurvesState) => {
    const source = displayUrl;
    if (!source) return;
    setIsSavingCurves(true);
    try {
      const blob = await applyCurvesToImageUrlAsBlob(source, curves, 'image/jpeg', 0.95);
      const url = URL.createObjectURL(blob);
      setAdjustedUrl(url);
      setIsCurvesOpen(false);
    } catch (e) {
      console.error('Apply curves failed', e);
      alert('Failed to apply curves. Please try again.');
    } finally {
      setIsSavingCurves(false);
    }
  };

  // Direct download function (local implementation as backup)
  const handleDirectDownload = (format: 'png' | 'jpeg') => {
    console.log('handleDirectDownload called:', format);
    
    if (!displayUrl) {
      console.error('No image available to download');
      alert('No image available to download');
      return;
    }

    try {
      // Try calling the passed onDownload prop first (pass displayUrl so parent uses adjusted image when set)
      if (onDownload) {
        console.log('Calling onDownload prop...');
        onDownload(format, watermark, displayUrl, includeAiDisclaimer);
      } else {
        // Fallback: direct download
        console.log('onDownload prop not available, using fallback');
        const link = document.createElement('a');
        link.href = displayUrl;
        link.download = `Photoclear-${Date.now()}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Download error:', error);
      // Ultimate fallback - open in new tab
      window.open(processedUrl, '_blank');
    }
    
    setShowDownloadMenu(false);
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSliderPosition(parseInt(e.target.value));
  };

  // Keyboard navigation for slider
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (viewMode === 'compare') {
        if (e.key === 'ArrowLeft') {
          setSliderPosition(prev => Math.max(0, prev - 5));
        } else if (e.key === 'ArrowRight') {
          setSliderPosition(prev => Math.min(100, prev + 5));
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewMode]);

  // Detect if image is 360 equirectangular
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      const ratio = img.width / img.height;
      const is360 = Math.abs(ratio - 2.0) < 0.1;
      setIs360Image(is360);
    };
    img.src = displayUrl;
  }, [displayUrl]);

  // Click outside to close download menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target as Node)) {
        setShowDownloadMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // --- Watermark Logic ---
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const url = URL.createObjectURL(file);
      setWatermark({
        imageUrl: url,
        x: 0.5, // Center
        y: 0.5,
        scale: 0.2, // 20% width
        opacity: 0.8
      });
      setViewMode('processed'); // Switch to view where watermark is visible
    }
  };

  const startDrag = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setIsDraggingLogo(true);
  };

  useEffect(() => {
    const handleMove = (e: MouseEvent | TouchEvent) => {
      if (!isDraggingLogo || !processedImageRef.current || !watermark) return;

      const rect = processedImageRef.current.getBoundingClientRect();
      const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;

      // Calculate relative position (0-1)
      let x = (clientX - rect.left) / rect.width;
      let y = (clientY - rect.top) / rect.height;

      // Clamp
      x = Math.max(0, Math.min(1, x));
      y = Math.max(0, Math.min(1, y));

      setWatermark(prev => prev ? { ...prev, x, y } : null);
    };

    const handleUp = () => {
      setIsDraggingLogo(false);
    };

    if (isDraggingLogo) {
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('touchmove', handleMove);
      window.addEventListener('mouseup', handleUp);
      window.addEventListener('touchend', handleUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchend', handleUp);
    };
  }, [isDraggingLogo, watermark]);

  return (
    <div className="flex flex-col h-full bg-black animate-fade-in">
       {/* Toolbar - Desktop */}
       <div className="hidden sm:flex h-16 bg-slate-900 border-b border-slate-800 items-center gap-2 px-4 shrink-0 shadow-sm z-40 relative">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-slate-400 hover:text-white font-medium transition-colors whitespace-nowrap shrink-0"
          >
            <ArrowLeft size={18} />
            <span className="hidden lg:inline">Back</span>
          </button>

          <div className="flex bg-slate-800 p-1 rounded-lg shrink-0">
             <button
               onClick={() => setViewMode('compare')}
               className="px-3 py-1.5 text-sm font-medium rounded-md transition-all whitespace-nowrap bg-slate-700 shadow text-white"
             >
               Before / After
             </button>
          </div>

          <div className="flex items-center gap-2 ml-auto min-w-0 justify-end">
            {/* Editing tools — grouped icon-only pill */}
            <div className="flex items-center gap-1 bg-slate-800/60 border border-slate-700 rounded-lg p-1 shrink-0">
              {is360Image && (
                <button
                  onClick={() => { setViewer360Mode('processed'); setShow360Viewer(true); }}
                  className="p-2 rounded-md transition-all text-blue-400 hover:bg-slate-700 hover:text-blue-300"
                  title="View 360°"
                >
                  <Eye size={18} />
                </button>
              )}

              <button
                onClick={() => setIsAdjusting(!isAdjusting)}
                className={`p-2 rounded-md transition-all ${isAdjusting ? 'bg-amber-900/30 text-amber-400' : 'text-slate-300 hover:bg-slate-700 hover:text-white'}`}
                title="Brighten"
              >
                <Sun size={18} />
              </button>

              <button
                onClick={() => setIsCurvesOpen(true)}
                className="p-2 rounded-md transition-all text-slate-300 hover:bg-slate-700 hover:text-white"
                title="Curves"
              >
                <SlidersHorizontal size={18} />
              </button>

              <button
                onClick={() => setIsWatermarking(!isWatermarking)}
                className={`p-2 rounded-md transition-all ${isWatermarking ? 'bg-orange-900/30 text-orange-400' : 'text-slate-300 hover:bg-slate-700 hover:text-white'}`}
                title="Watermark"
              >
                <Stamp size={18} />
              </button>

              <button
                onClick={() => window.open(window.location.origin + '/', '_blank')}
                className="p-2 rounded-md transition-all text-slate-300 hover:bg-slate-700 hover:text-white"
                title="Edit another image (opens in new tab)"
              >
                <ImagePlus size={18} />
              </button>
            </div>

            {onSendToPhotoplan && (
              <button
                type="button"
                onClick={onSendToPhotoplan}
                className="px-4 py-2 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-sm font-semibold"
                title={sendToPhotoplanLabel}
              >
                {sendToPhotoplanLabel}
              </button>
            )}

            {/* Send-to-app logos */}
            {(onSendToVirtualStaging || onSendToHyperdusk || onSendToPhotoEdit) && (
              <div className="flex items-center gap-2 shrink-0">
                {onSendToPhotoEdit && (
                  <button
                    onClick={onSendToPhotoEdit}
                    className="group relative px-3 py-2 rounded-lg flex items-center transition-all whitespace-nowrap shrink-0 bg-black hover:bg-slate-950 border border-sky-500/40 hover:border-sky-400"
                    title="Send to PhotoEdit"
                  >
                    <img
                      src="https://photoedit.rehub.studio/photoedit-logo.png"
                      alt="PhotoEdit"
                      className="h-6 w-auto object-contain"
                    />
                  </button>
                )}
                {onSendToVirtualStaging && (
                  <button
                    onClick={onSendToVirtualStaging}
                    className="group relative px-3 py-2 rounded-lg flex items-center transition-all whitespace-nowrap shrink-0 bg-black hover:bg-slate-950 border border-purple-500/40 hover:border-purple-400 shadow-[0_0_12px_-2px_rgba(168,85,247,0.5)] hover:shadow-[0_0_18px_-2px_rgba(168,85,247,0.8)]"
                    title="Send to Virtual Staging Studio"
                  >
                    <img
                      src="/virtual-staging-logo-transparent.png"
                      alt="Virtual Staging Studio"
                      className="h-6 w-auto object-contain"
                    />
                  </button>
                )}

                {onSendToHyperdusk && (
                  <button
                    onClick={onSendToHyperdusk}
                    className="group relative px-3 py-2 rounded-lg flex items-center transition-all whitespace-nowrap shrink-0 bg-black hover:bg-slate-950 border border-indigo-500/40 hover:border-indigo-400 shadow-[0_0_12px_-2px_rgba(99,102,241,0.5)] hover:shadow-[0_0_18px_-2px_rgba(99,102,241,0.8)]"
                    title="Send to Hyper Dusk for day-to-night conversion"
                  >
                    <img
                      src="/hyperdusk-logo-transparent.png"
                      alt="Hyper Dusk"
                      className="h-6 w-auto object-contain"
                    />
                  </button>
                )}
              </div>
            )}

            <div className="h-8 w-px bg-slate-800 shrink-0" />

            <div className="shrink-0 flex items-center">
              <AIContentDisclosure
                classification={aiClassification}
                operation={aiOperation}
                app="photoclear"
              />
            </div>

            {/* Primary actions */}
            <button
              onClick={() => onAccept(displayUrl)}
              className="bg-slate-700 hover:bg-slate-600 text-white px-3 py-2 rounded-lg font-medium flex items-center gap-2 transition-all shadow-md hover:shadow-lg whitespace-nowrap shrink-0"
              title="Keep this edit and make more changes"
            >
              <Layers size={18} />
              <span className="hidden lg:inline">Continue Editing</span>
            </button>

            <div className="relative shrink-0" ref={downloadMenuRef}>
              <button
                onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                className="px-3 py-2 rounded-lg font-medium flex items-center gap-2 transition-all shadow-md bg-orange-600 hover:bg-orange-500 text-white hover:shadow-lg whitespace-nowrap"
              >
                <Download size={18} />
                <span className="hidden lg:inline">{t('common.download')}</span>
                <ChevronDown size={14} className={`transition-transform duration-200 ${showDownloadMenu ? 'rotate-180' : ''}`} />
              </button>
              
              {showDownloadMenu && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-slate-900 rounded-lg shadow-xl border border-slate-700 py-2 z-50 animate-in fade-in zoom-in-95 duration-100 origin-top-right">
                   <label
                     className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 cursor-pointer border-b border-slate-800 mb-1"
                     onMouseDown={(e) => e.stopPropagation()}
                   >
                     <input
                       type="checkbox"
                       checked={includeAiDisclaimer}
                       onChange={(e) => setIncludeAiDisclaimer(e.target.checked)}
                       className="rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500"
                     />
                     <span>Add “{getDownloadLabel(aiClassification)}” label</span>
                   </label>
                   {/* Standard Downloads */}
                   <div className="px-4 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Download Format
                   </div>
                   <button 
                     type="button"
                     onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleDirectDownload('jpeg'); }}
                     className="w-full text-left px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors flex items-center justify-between group cursor-pointer"
                   >
                     <span>JPEG Image</span>
                     <span className="text-[10px] bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded border border-slate-700 group-hover:bg-slate-700">Smaller</span>
                   </button>
                   <button 
                     type="button"
                     onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleDirectDownload('png'); }}
                     className="w-full text-left px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors flex items-center justify-between group cursor-pointer"
                   >
                     <span>PNG Image</span>
                     <span className="text-[10px] bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded border border-slate-700 group-hover:bg-slate-700">Lossless</span>
                   </button>

                </div>
              )}
            </div>
          </div>
       </div>

       {/* Toolbar - Mobile */}
       <div className="sm:hidden bg-slate-900 border-b border-slate-800 shrink-0 shadow-sm z-40">
          {/* Top row: Back + View Mode */}
          <div className="flex items-center justify-between px-3 py-2">
            <button 
              onClick={onBack}
              className="flex items-center gap-1.5 text-slate-400 hover:text-white text-sm font-medium transition-colors"
            >
              <ArrowLeft size={16} />
              <span>Back</span>
            </button>

            <div className="flex bg-slate-800 p-0.5 rounded-lg">
               <button
                 onClick={() => setViewMode('compare')}
                 className="px-2.5 py-1 text-xs font-medium rounded transition-all bg-slate-700 shadow text-white"
               >
                 Before / After
               </button>
            </div>
          </div>
       </div>

       {/* Brighten / Adjust Panel - Mobile */}
       {isAdjusting && (
          <div className="sm:hidden bg-slate-900 border-b border-slate-800 px-3 py-3 animate-slide-down space-y-3">
            <p className="text-[10px] text-slate-500">Live preview — image updates as you move sliders; Apply to save.</p>
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-400 w-16">Brightness</span>
              <input
                type="range"
                min="-100"
                max="100"
                value={brightness}
                onChange={(e) => setBrightness(Number(e.target.value))}
                className="flex-1 accent-amber-500"
              />
              <span className="text-xs text-slate-400 tabular-nums w-8">{brightness}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-400 w-16">Contrast</span>
              <input
                type="range"
                min="50"
                max="200"
                value={Math.round(contrast * 100)}
                onChange={(e) => setContrast(Number(e.target.value) / 100)}
                className="flex-1 accent-amber-500"
              />
              <span className="text-xs text-slate-400 tabular-nums w-8">{Math.round(contrast * 100)}%</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleApplyAdjustments}
                disabled={isApplyingAdjustments}
                className="flex-1 py-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
              >
                {isApplyingAdjustments ? 'Applying…' : 'Apply'}
              </button>
              {adjustedUrl && (
                <button onClick={handleResetAdjustments} className="px-3 py-2 text-slate-400 text-sm">
                  Reset
                </button>
              )}
            </div>
          </div>
       )}

       {/* Mobile Bottom Action Bar */}
       <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-800 px-3 py-3 z-50 flex items-center justify-between gap-2">
          <button
            onClick={() => setIsAdjusting(!isAdjusting)}
            className={`p-2.5 rounded-lg transition-all ${isAdjusting ? 'bg-amber-900/30 text-amber-500 border border-amber-800' : 'bg-slate-800 text-slate-400 border border-slate-700'}`}
          >
            <Sun size={20} />
          </button>
          <button
            onClick={() => setIsCurvesOpen(true)}
            className="p-2.5 rounded-lg transition-all bg-slate-800 text-slate-400 border border-slate-700"
            title="Curves"
          >
            <SlidersHorizontal size={20} />
          </button>
          <button
            onClick={() => setIsWatermarking(!isWatermarking)}
            className={`p-2.5 rounded-lg transition-all ${isWatermarking ? 'bg-orange-900/30 text-orange-500 border border-orange-800' : 'bg-slate-800 text-slate-400 border border-slate-700'}`}
          >
            <Stamp size={20} />
          </button>

          <button
            onClick={() => onAccept(displayUrl)}
            className="flex-1 bg-slate-700 text-white py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 text-sm"
          >
            <Layers size={16} />
            Continue Editing
          </button>

          <button
            onClick={() => window.open(window.location.origin + '/', '_blank')}
            className="p-2.5 rounded-lg bg-slate-800 text-slate-400 border border-slate-700"
            title="Edit another image"
          >
            <ImagePlus size={20} />
          </button>

          {onSendToPhotoplan && (
            <button
              type="button"
              onClick={onSendToPhotoplan}
              className="px-3 py-2.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-sm font-semibold"
              title={sendToPhotoplanLabel}
            >
              {sendToPhotoplanLabel}
            </button>
          )}

          {onSendToPhotoEdit && (
            <button
              onClick={onSendToPhotoEdit}
              className="px-2 py-2 rounded-lg bg-black border border-sky-500/40 flex items-center"
              title="Send to PhotoEdit"
            >
              <img src="https://photoedit.rehub.studio/photoedit-logo.png" alt="PhotoEdit" className="h-5 w-auto object-contain" />
            </button>
          )}

          {onSendToVirtualStaging && (
            <button
              onClick={onSendToVirtualStaging}
              className="px-2 py-2 rounded-lg bg-black border border-purple-500/40 shadow-[0_0_10px_-2px_rgba(168,85,247,0.5)] flex items-center"
              title="Send to Virtual Staging Studio"
            >
              <img src="/virtual-staging-logo-transparent.png" alt="Virtual Staging Studio" className="h-5 w-auto object-contain" />
            </button>
          )}

          {onSendToHyperdusk && (
            <button
              onClick={onSendToHyperdusk}
              className="px-2 py-2 rounded-lg bg-black border border-indigo-500/40 shadow-[0_0_10px_-2px_rgba(99,102,241,0.5)] flex items-center"
              title="Send to Hyper Dusk"
            >
              <img src="/hyperdusk-logo-transparent.png" alt="Hyper Dusk" className="h-5 w-auto object-contain" />
            </button>
          )}

          <div className="relative" ref={downloadMenuRef}>
            <button
              onClick={() => setShowDownloadMenu(!showDownloadMenu)}
              className="bg-orange-600 text-white py-2.5 px-4 rounded-lg font-medium flex items-center gap-2 text-sm"
            >
              <Download size={16} />
              {t('common.download')}
            </button>
            
            {showDownloadMenu && (
              <div className="absolute right-0 bottom-full mb-2 w-56 bg-slate-900 rounded-lg shadow-xl border border-slate-700 py-2 z-50">
                 <label
                   className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 cursor-pointer border-b border-slate-800 mb-1"
                   onMouseDown={(e) => e.stopPropagation()}
                 >
                   <input
                     type="checkbox"
                     checked={includeAiDisclaimer}
                     onChange={(e) => setIncludeAiDisclaimer(e.target.checked)}
                     className="rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500"
                   />
                   <span>Add “{getDownloadLabel(aiClassification)}” label</span>
                 </label>
                 <button 
                   type="button"
                   onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleDirectDownload('jpeg'); }}
                   className="w-full text-left px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors cursor-pointer"
                 >
                   JPEG (Smaller)
                 </button>
                 <button 
                   type="button"
                   onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleDirectDownload('png'); }}
                   className="w-full text-left px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors cursor-pointer"
                 >
                   PNG (Lossless)
                 </button>
              </div>
            )}
          </div>
       </div>
       
       {/* Brighten / Adjust Panel - Desktop */}
       {isAdjusting && (
          <div className="hidden sm:flex bg-slate-900 border-b border-slate-800 px-6 py-3 items-center gap-6 animate-slide-down">
            <span className="text-xs text-slate-500 mr-2">Live preview — image updates as you move sliders; Apply to save.</span>
            <span className="text-sm font-medium text-slate-300">Brightness</span>
            <input
              type="range"
              min="-100"
              max="100"
              value={brightness}
              onChange={(e) => setBrightness(Number(e.target.value))}
              className="w-28 accent-amber-500"
            />
            <span className="text-xs text-slate-400 tabular-nums">{brightness}</span>
            <span className="text-sm font-medium text-slate-300">Contrast</span>
            <input
              type="range"
              min="50"
              max="200"
              value={Math.round(contrast * 100)}
              onChange={(e) => setContrast(Number(e.target.value) / 100)}
              className="w-28 accent-amber-500"
            />
            <span className="text-xs text-slate-400 tabular-nums">{Math.round(contrast * 100)}%</span>
            <button
              onClick={handleApplyAdjustments}
              disabled={isApplyingAdjustments}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
            >
              {isApplyingAdjustments ? 'Applying…' : 'Apply'}
            </button>
            {adjustedUrl && (
              <button
                onClick={handleResetAdjustments}
                className="px-3 py-2 text-slate-400 hover:text-white text-sm"
              >
                Reset
              </button>
            )}
          </div>
       )}

       {/* Watermark Controls Panel - Desktop */}
       {isWatermarking && (
          <div className="hidden sm:flex bg-slate-900 border-b border-slate-800 px-6 py-3 items-center gap-6 animate-slide-down">
              <input 
                type="file" 
                ref={logoInputRef}
                accept="image/png, image/jpeg, image/webp"
                className="hidden"
                onChange={handleLogoUpload}
              />
              
              {!watermark ? (
                <div className="flex items-center gap-3">
                  {userId && (
                    <BrandProfilePicker
                      brands={brands}
                      loading={brandsLoading}
                      onSelect={handleBrandSelect}
                      onManage={() => setShowBrandManager(true)}
                      accentColor="#f97316"
                      compact
                    />
                  )}
                  <button 
                    onClick={() => logoInputRef.current?.click()}
                    className="flex items-center gap-2 px-4 py-2 bg-orange-900/30 text-orange-400 rounded-lg hover:bg-orange-900/50 transition-colors text-sm font-medium border border-orange-800/50"
                  >
                    <Upload size={16} />
                    Upload Logo
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-6 w-full">
                  <div className="flex items-center gap-2">
                     <img src={watermark.imageUrl} alt="Logo" className="w-8 h-8 object-contain rounded bg-slate-800 border border-slate-700" />
                     <button 
                       onClick={() => setWatermark(null)}
                       className="text-red-400 hover:bg-red-900/30 p-1.5 rounded-md transition-colors"
                     >
                       <Trash2 size={16} />
                     </button>
                  </div>

                  <div className="h-8 w-px bg-slate-800"></div>

                  <div className="flex items-center gap-3">
                     <span className="text-xs font-medium text-slate-400">Opacity</span>
                     <input 
                       type="range" 
                       min="10" 
                       max="100" 
                       value={watermark.opacity * 100}
                       onChange={(e) => setWatermark({...watermark, opacity: parseInt(e.target.value) / 100})}
                       className="w-32 accent-orange-500"
                     />
                     <span className="text-xs w-8 text-right text-slate-300">{Math.round(watermark.opacity * 100)}%</span>
                  </div>

                  <div className="flex items-center gap-3">
                     <span className="text-xs font-medium text-slate-400">Size</span>
                     <input 
                       type="range" 
                       min="5" 
                       max="80" 
                       value={watermark.scale * 100}
                       onChange={(e) => setWatermark({...watermark, scale: parseInt(e.target.value) / 100})}
                       className="w-32 accent-orange-500"
                     />
                     <span className="text-xs w-8 text-right text-slate-300">{Math.round(watermark.scale * 100)}%</span>
                  </div>

                  <div className="ml-auto text-xs text-orange-400 bg-orange-900/20 px-3 py-1.5 rounded-full border border-orange-900/50 flex items-center gap-1.5">
                    <Move size={12} />
                    Drag logo on image to position
                  </div>
                </div>
              )}
          </div>
       )}

       {/* Watermark Controls Panel - Mobile */}
       {isWatermarking && (
          <div className="sm:hidden bg-slate-900 border-b border-slate-800 px-3 py-2 animate-slide-down">
              <input 
                type="file" 
                ref={logoInputRef}
                accept="image/png, image/jpeg, image/webp"
                className="hidden"
                onChange={handleLogoUpload}
              />
              
              {!watermark ? (
                <div className="space-y-2">
                  {userId && (
                    <BrandProfilePicker
                      brands={brands}
                      loading={brandsLoading}
                      onSelect={handleBrandSelect}
                      onManage={() => setShowBrandManager(true)}
                      accentColor="#f97316"
                    />
                  )}
                  <button 
                    onClick={() => logoInputRef.current?.click()}
                    className="flex items-center gap-2 px-3 py-2 bg-orange-900/30 text-orange-400 rounded-lg text-sm font-medium border border-orange-800/50 w-full justify-center"
                  >
                    <Upload size={16} />
                    Upload Logo
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                       <img src={watermark.imageUrl} alt="Logo" className="w-8 h-8 object-contain rounded bg-slate-800 border border-slate-700" />
                       <span className="text-xs text-slate-400">Drag on image to move</span>
                    </div>
                    <button 
                      onClick={() => setWatermark(null)}
                      className="text-red-400 bg-red-900/20 p-1.5 rounded-md"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-1 flex items-center gap-2">
                       <span className="text-[10px] font-medium text-slate-500">Opacity</span>
                       <input 
                         type="range" 
                         min="10" 
                         max="100" 
                         value={watermark.opacity * 100}
                         onChange={(e) => setWatermark({...watermark, opacity: parseInt(e.target.value) / 100})}
                         className="flex-1 accent-orange-500"
                       />
                    </div>

                    <div className="flex-1 flex items-center gap-2">
                       <span className="text-[10px] font-medium text-slate-500">Size</span>
                       <input 
                         type="range" 
                         min="5" 
                         max="80" 
                         value={watermark.scale * 100}
                         onChange={(e) => setWatermark({...watermark, scale: parseInt(e.target.value) / 100})}
                         className="flex-1 accent-orange-500"
                       />
                    </div>
                  </div>
                </div>
              )}
          </div>
       )}

       {/* View Area */}
       <div className="flex-1 overflow-hidden p-4 sm:p-8 pb-20 sm:pb-8 flex items-center justify-center bg-black">
          <div className="relative shadow-2xl rounded-lg overflow-hidden bg-slate-900 ring-1 ring-white/10 w-fit h-fit flex justify-center">
            
            {viewMode === 'processed' && (
              <div className="relative group">
                <img 
                  ref={processedImageRef}
                  src={displayUrl} 
                  alt="Processed" 
                  className="max-w-full max-h-[80vh] object-contain block" 
                  style={previewFilter ? { filter: previewFilter, transition: 'filter 0.1s ease-out' } : undefined}
                />
                {includeAiDisclaimer && (
                  <div className="absolute bottom-2 right-2 z-20 pointer-events-none rounded-full bg-black/45 px-2.5 py-1 text-[10px] sm:text-xs font-medium text-white/90 tracking-wide">
                    {getDownloadLabel(aiClassification)}
                  </div>
                )}
                {watermark && (
                  <>
                    <div
                      className={`absolute select-none origin-center ${
                        isDraggingLogo 
                          ? 'cursor-grabbing z-50' 
                          : 'cursor-move z-40 hover:z-50'
                      }`}
                      style={{
                        left: `${watermark.x * 100}%`,
                        top: `${watermark.y * 100}%`,
                        width: `${watermark.scale * 100}%`,
                        transform: 'translate(0, 0)',
                        touchAction: 'none'
                      }}
                      onMouseDown={startDrag}
                      onTouchStart={startDrag}
                    >
                         {/* Visual wrapper for the image */}
                         <div className={`
                             w-full h-full relative transition-all duration-200
                             ${isDraggingLogo 
                               ? 'ring-2 ring-orange-500/80 shadow-[0_0_20px_rgba(249,115,22,0.3)] bg-orange-500/5 backdrop-blur-[1px] rounded-sm' 
                               : 'hover:ring-1 hover:ring-white/40 rounded-sm'
                             }
                         `}>
                             <img 
                               src={watermark.imageUrl}
                               alt="Watermark"
                               className="w-full h-full object-contain pointer-events-none"
                               style={{ opacity: watermark.opacity }}
                             />
                             
                             {/* Corner markers for drag indication */}
                             {isDraggingLogo && (
                                <>
                                    <div className="absolute -top-1 -left-1 w-2.5 h-2.5 bg-orange-500 border border-white rounded-full shadow-sm"></div>
                                    <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-orange-500 border border-white rounded-full shadow-sm"></div>
                                    <div className="absolute -bottom-1 -left-1 w-2.5 h-2.5 bg-orange-500 border border-white rounded-full shadow-sm"></div>
                                    <div className="absolute -bottom-1 -right-1 w-2.5 h-2.5 bg-orange-500 border border-white rounded-full shadow-sm"></div>
                                </>
                             )}
                         </div>
                    </div>

                    {/* Helper Tooltip when dragging */}
                    {isDraggingLogo && (
                        <div 
                            className="absolute pointer-events-none z-50 bg-slate-900/95 text-orange-200 text-[10px] font-medium px-2.5 py-1.5 rounded-md shadow-xl backdrop-blur-md border border-orange-500/30 flex items-center gap-2 transform -translate-x-1/2 -translate-y-full mt-[-8px]"
                            style={{
                                left: `${(watermark.x + watermark.scale/2) * 100}%`,
                                top: `${watermark.y * 100}%`,
                            }}
                        >
                            <Move size={10} className="text-orange-400" />
                            <span>
                                X: {Math.round(watermark.x * 100)}% Y: {Math.round(watermark.y * 100)}%
                            </span>
                        </div>
                    )}

                    {/* Hover Hint when not dragging */}
                    {!isDraggingLogo && (
                        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/70 text-white text-xs px-3 py-1.5 rounded-full backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none flex items-center gap-1.5 border border-white/10 z-30">
                            <Move size={12} />
                            Drag watermark to reposition
                        </div>
                    )}
                  </>
                )}
              </div>
            )}

            {viewMode === 'original' && (
               <img src={originalUrl} alt="Original" className="max-w-full max-h-[80vh] object-contain block" />
            )}

            {viewMode === 'compare' && (
              <div ref={containerRef} className="relative select-none group w-fit h-fit">
                 <img 
                    src={displayUrl} 
                    alt="After" 
                    className="max-w-full max-h-[80vh] object-contain block select-none pointer-events-none" 
                    draggable={false}
                    style={previewFilter ? { filter: previewFilter, transition: 'filter 0.1s ease-out' } : undefined}
                 />

                 {/* Top Image: Original - Clipped */}
                 <img 
                    src={originalUrl} 
                    alt="Before" 
                    className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none" 
                    style={{ 
                      clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` 
                    }}
                 />

                 {/* Divider Line */}
                 <div 
                    className="absolute inset-y-0 w-0.5 bg-white shadow-[0_0_15px_rgba(0,0,0,0.5)] cursor-ew-resize z-20 pointer-events-none"
                    style={{ left: `${sliderPosition}%` }}
                 >
                    {/* Handle Button */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-slate-400">
                      <ChevronLeft size={14} className="text-slate-400" />
                      <GripVertical size={14} className="text-slate-300 mx-[-2px]" />
                      <ChevronRight size={14} className="text-slate-400" />
                    </div>
                 </div>

                 {/* Interactive Input Overlay */}
                 <input 
                   type="range"
                   min="0"
                   max="100"
                   value={sliderPosition}
                   onChange={handleSliderChange}
                   className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize z-30 touch-none"
                   aria-label="Comparison slider"
                 />
                 
                 {/* Floating Labels */}
                 <div className="absolute top-4 left-4 bg-black/60 text-white text-xs font-semibold px-3 py-1.5 rounded-full backdrop-blur-md shadow-sm border border-white/10 pointer-events-none transition-opacity duration-300" style={{ opacity: sliderPosition < 10 ? 0 : 1 }}>
                    Original
                 </div>
                 <div className="absolute top-4 right-4 bg-orange-600/80 text-white text-xs font-semibold px-3 py-1.5 rounded-full backdrop-blur-md shadow-sm border border-white/10 pointer-events-none transition-opacity duration-300" style={{ opacity: sliderPosition > 90 ? 0 : 1 }}>
                    Decluttered
                 </div>
                 {includeAiDisclaimer && (
                   <div className="absolute bottom-2 right-2 z-20 pointer-events-none rounded-full bg-black/45 px-2.5 py-1 text-[10px] sm:text-xs font-medium text-white/90 tracking-wide">
                     {getDownloadLabel(aiClassification)}
                   </div>
                 )}
              </div>
            )}
          </div>
       </div>
       
       {/* Curves Editor Modal */}
       {isCurvesOpen && displayUrl && (
         <CurvesEditor
           imageUrl={displayUrl}
           isSaving={isSavingCurves}
           onSave={handleSaveCurves}
           onCancel={() => !isSavingCurves && setIsCurvesOpen(false)}
         />
       )}

       {/* 360 Viewer Modal */}
       {show360Viewer && (
         <Viewer360 
           imageUrl={viewer360Mode === 'original' ? originalUrl : displayUrl}
           onClose={() => setShow360Viewer(false)}
           title={viewer360Mode === 'original' ? '360° Original' : '360° Result'}
         />
       )}

       {/* Brand Manager Modal */}
       {showBrandManager && userId && (
         <MediaBrandManager
           userId={userId}
           brands={brands}
           loading={brandsLoading}
           onClose={() => setShowBrandManager(false)}
           onLoadBrands={loadBrands}
           onCreateBrand={(input) => createBrandProfile(supabase, userId, input)}
           onUpdateBrand={(id, input) => updateBrandProfile(supabase, userId, id, input)}
           onDeleteBrand={(id) => deleteBrandProfile(supabase, userId, id)}
           onUploadLogo={(file) => uploadBrandProfileLogo(supabase, file, userId)}
           applyMode
           onBrandApplied={(brand) => { handleBrandSelect(brand); setShowBrandManager(false); }}
           accentColor="#f97316"
           mode="watermark"
         />
       )}
    </div>
  );
};

export default ResultViewer;
